import React, { useState, useEffect } from 'react';
import userService from '../../services/UserService.js';

const UserProfileModal = ({ isOpen, onClose, userId }) => {
  const [fullUserData, setFullUserData] = useState(null);
  const [loadingUserData, setLoadingUserData] = useState(false);

  useEffect(() => {
    if (isOpen && userId && !fullUserData) {
      fetchFullUserData();
    }
  }, [isOpen, userId]);

  const fetchFullUserData = async () => {
    try {
      setLoadingUserData(true);
      const response = await userService.getById(userId);
      setFullUserData(response.data);
    } catch (err) {
      console.error('Failed to load user details:', err);
    } finally {
      setLoadingUserData(false);
    }
  };

  const getProfileImageUrl = (userData) => {
    if (userData?.profile_photo_base64) {
      return `data:image/jpeg;base64,${userData.profile_photo_base64}`;
    }
    if (userData?.user_id) {
      return userService.getProfilePhoto(userData.user_id);
    }
    return null;
  };

  const capitalizeStatus = (status) => {
    return status ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() : '';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-start sm:items-center justify-center p-0 sm:p-4 overflow-y-auto">
      <div className="bg-white w-full sm:max-w-4xl sm:rounded-lg shadow-xl min-h-screen sm:min-h-0 sm:my-8 max-h-screen sm:max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900">My Profile</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Modal Content */}
        {loadingUserData ? (
          <div className="flex items-center justify-center py-12 sm:py-20">
            <div className="text-center">
              <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
              <p className="text-gray-600 text-sm sm:text-base">Loading profile...</p>
            </div>
          </div>
        ) : fullUserData ? (
          <>
            {/* Profile Header Section */}
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-4 sm:p-8">
              <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                <div className="w-20 h-20 sm:w-32 sm:h-32 rounded-full border-4 border-white overflow-hidden bg-white flex items-center justify-center flex-shrink-0">
                  <img
                    src={getProfileImageUrl(fullUserData)}
                    alt={fullUserData.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = `data:image/svg+xml,${encodeURIComponent(`
                        <svg xmlns="http://www.w3.org/2000/svg" width="128" height="128" viewBox="0 0 128 128">
                          <rect width="128" height="128" fill="#e5e7eb"/>
                          <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="#6b7280" font-family="Arial" font-size="48" font-weight="bold">
                            ${fullUserData.name.charAt(0).toUpperCase()}
                          </text>
                        </svg>
                      `)}`;
                    }}
                  />
                </div>
                <div className="flex-1 text-center sm:text-left">
                  <h2 className="text-xl sm:text-3xl font-bold text-white mb-2">{fullUserData.name}</h2>
                  <p className="text-orange-100 text-base sm:text-lg mb-3">{fullUserData.role_name}</p>
                  <div className="flex items-center justify-center sm:justify-start gap-3">
                    <span className={`px-3 sm:px-4 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-semibold ${
                      fullUserData.status === 'active' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {capitalizeStatus(fullUserData.status)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Details Sections */}
            <div className="p-4 sm:p-8 space-y-6 sm:space-y-8">
              {/* Basic Information */}
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 pb-2 border-b-2 border-orange-500">
                  Basic Information
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-1">Username</label>
                    <p className="text-sm sm:text-base text-gray-900 font-medium break-all">{fullUserData.username || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-1">Full Name</label>
                    <p className="text-sm sm:text-base text-gray-900 font-medium">{fullUserData.name}</p>
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-1">Email</label>
                    <p className="text-sm sm:text-base text-gray-900 font-medium break-all">{fullUserData.email}</p>
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-1">Date of Birth</label>
                    <p className="text-sm sm:text-base text-gray-900 font-medium">{formatDate(fullUserData.dob)}</p>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 pb-2 border-b-2 border-orange-500">
                  Contact Information
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-1">Contact Number</label>
                    <p className="text-sm sm:text-base text-gray-900 font-medium">{fullUserData.contact}</p>
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-1">City</label>
                    <p className="text-sm sm:text-base text-gray-900 font-medium">{fullUserData.city}</p>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-1">Location/Address</label>
                    <p className="text-sm sm:text-base text-gray-900 font-medium">{fullUserData.location}</p>
                  </div>
                </div>
              </div>

              {/* Document Information */}
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 pb-2 border-b-2 border-orange-500">
                  Document Information
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-1">Document Type</label>
                    <p className="text-sm sm:text-base text-gray-900 font-medium">
                      {fullUserData.document_type ? fullUserData.document_type.replace(/_/g, ' ').toUpperCase() : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-1">Document Number</label>
                    <p className="text-sm sm:text-base text-gray-900 font-medium break-all">{fullUserData.document_number || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Role and Status */}
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 pb-2 border-b-2 border-orange-500">
                  Role and Status
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-1">Role</label>
                    <p className="text-sm sm:text-base text-gray-900 font-medium">{fullUserData.role_name}</p>
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-1">Status</label>
                    <p className="text-sm sm:text-base text-gray-900 font-medium">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        fullUserData.status === 'active' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {capitalizeStatus(fullUserData.status)}
                      </span>
                    </p>
                  </div>
                </div>
              </div>

              {/* System Information */}
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 pb-2 border-b-2 border-orange-500">
                  System Information
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-1">User ID</label>
                    <p className="text-sm sm:text-base text-gray-900 font-medium break-all">{fullUserData.user_id}</p>
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-1">Created By</label>
                    <p className="text-sm sm:text-base text-gray-900 font-medium">{fullUserData.created_by || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-1">Created At</label>
                    <p className="text-sm sm:text-base text-gray-900 font-medium">{formatDate(fullUserData.created_at)}</p>
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-1">Last Updated</label>
                    <p className="text-sm sm:text-base text-gray-900 font-medium">{formatDate(fullUserData.updated_at)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-gray-50 px-4 sm:px-8 py-4 sm:py-6 border-t">
              <button
                onClick={onClose}
                className="w-full px-4 sm:px-6 py-2.5 sm:py-3 border border-gray-300 text-gray-900 rounded-lg font-semibold hover:bg-gray-100 transition-colors text-sm sm:text-base"
              >
                Close
              </button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default UserProfileModal;